import { useEffect, useState } from 'react';
import axios from 'common/AxiosAuth';
import { LargeButton, InfoMessage, DescriptionBar, MenuTab, Grid, Button, Modal, ModalInner } from 'components';
import { Message } from 'types/message';

import { InformationCircleOutlineIcon } from 'assets/svgs';
import { PaidScheduleGridData, } from './types';
import { paidScheduleGrid, s3Status, s3LinkStatus } from './utils';

const PaidSchedule = () => {
  const [messageArray, setMessageArray] = useState<Message[]>([]);
  const [messageArrayCount, setMessageArrayCount] = useState(0);
  const [paidScheduleGridData, setPaidScheduleGridData] = useState<PaidScheduleGridData[]>([]);
  const pageSize = 10; // 페이징처리 기준
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [downloadLinks, setDownloadLinks] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    // 페이지가 변경될 때마다 데이터 요청
    getPaidSchedule(currentPage);
  }, [currentPage]);

  const getPaidSchedule= async (pageNum: number) => {
    const res = await axios.get(`/api/admin/schedule/paid/${pageNum}`);
    const { scheduleList = [], totalElement = 0 } = res.data;
    console.log('PaidscheduleList Data:', scheduleList); 
    const paidScheduleGridData = scheduleList.map((item: any) =>{
      return {
        id:item.id,
        memberId:item.memberId,
        startTime:item.startTime,
        endTime:item.endTime,
        status:item.status,
        direction:item.direction,
        band:item.band,
        name:item.name,
        email:item.email,
        isPayment:item.isPayment,
        isSendMailS3:item.isSendMailS3
      };
    });
    setPaidScheduleGridData(paidScheduleGridData);
    // 총 페이지 수 계산
    const totalPages = Math.ceil(totalElement / pageSize);
    setTotalPages(totalPages);
  };

  // 링크 검증 함수 (문자열만 확인)
  const validateDownloadLink = (link: string): { isValid: boolean; message: string } => {
    if (!link.trim()) {
      return { isValid: false, message: '다운로드 링크를 입력해주세요.' };
    }

    return { isValid: true, message: '' };
  };

  // 확인 모달 상태
  const [isConfirmModalOpened, setIsConfirmModalOpened] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<PaidScheduleGridData | null>(null);

  // Send 버튼 클릭 시 확인 모달 열기
  const handleSendClick = (data: PaidScheduleGridData) => {
    const downloadLink = downloadLinks[data.id] || '';
    if (!downloadLink.trim()) {
      return; // 빈 링크면 아무것도 안함
    }
    setSelectedRowData(data);
    setIsConfirmModalOpened(true);
  };

  // 실제 전송 함수
  const onClickSend = async () => {
    if (!selectedRowData) return;
    
    setIsLoading(true);
    try {
      const scheduleId = selectedRowData.id;
      const downloadLink = downloadLinks[selectedRowData.id] || '';
      
      console.log('Sending request:', { scheduleId, downloadLink });
      
      const response = await axios.post(`/api/admin/schedule/saveDownloadLink/${scheduleId}`, {
        downloadLink: downloadLink
      });
      
      console.log('S3 Link saved successfully:', response.data);
      
      // 성공 시 입력 필드 초기화
      setDownloadLinks(prev => ({ ...prev, [selectedRowData.id]: '' }));
      
      // 데이터 새로고침
      getPaidSchedule(currentPage);
      
      // 모달 닫기
      setIsConfirmModalOpened(false);
      setSelectedRowData(null);
      
    } catch (error: any) {
      console.error('Failed to save S3 Link:', error);
    } finally {
      setIsLoading(false);
    }
  };


  const handleDownloadLinkChange = (id: string, value: string) => {
    setDownloadLinks(prev => ({ ...prev, [id]: value }));
  };

  return (
    <div className="flex-1">
      <Grid
        gridColumns={paidScheduleGrid}
        gridData={paidScheduleGridData}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        options={{
          useFilter: false,
          useCheckbox: false,
          usePagination: {
            use: true,
            maxPage: totalPages, // 총 페이지 수 반영
          },
          customCell: {
            status: (value) => {
              const status = s3Status[value] || s3Status.UNKNOWN;
              return (
                <div title={value}>
                  <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
                    <span className="text-nowrap text-small font-regular">{status.title}</span>
                  </div>
                </div>
              );
            },
            isSendMailS3: (value) => {
              const status = s3LinkStatus[value.toString()];
              return (
                <div title={value ? 'Link Sent' : 'Link Not Sent'}>
                  <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
                    <span className="text-nowrap text-small font-regular">{status.title}</span>
                  </div>
                </div>
              );
            },

            s3Link: (_, row, index) => {
              const currentLink = downloadLinks[row.id] || '';
              const hasValue = currentLink.trim().length > 0;
              
              return (
                <div className="flex items-center space-x-2">
                  <textarea
                    value={currentLink}
                    onChange={(e) => handleDownloadLinkChange(row.id, e.target.value)}
                    placeholder="다운로드 링크를 입력하세요"
                    className="min-w-[580px] min-h-[120px] border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <Button
                    text={isLoading ? "Sending..." : "Send"}
                    type="secondary"
                    size="medium"
                    onClick={() => handleSendClick(row as PaidScheduleGridData)}
                    disabled={isLoading || !hasValue}
                  />
                </div>
              );
            }
          },
        }}
      />
      <InfoMessage messageArray={messageArray} onClickDelete={() => {}} />
      
      {/* 링크 확인 모달 */}
      {isConfirmModalOpened && selectedRowData && (
        <Modal isOpened={isConfirmModalOpened}>
          <ModalInner className="md:w-[600px]" title="링크 전송 확인" onClickClose={() => setIsConfirmModalOpened(false)}>
            <div className="p-6 pt-0">
              <div className="mb-4">
                <p className="text-body1 font-medium mb-2">다음 링크를 전송하시겠습니까?</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="mb-2">
                  <span className="text-sm font-medium text-gray-700">수신자:</span>
                  <span className="ml-2 text-sm text-gray-900">{selectedRowData.name} ({selectedRowData.email})</span>
                </div>
                <div className="mb-2">
                  <span className="text-sm font-medium text-gray-700">스케줄 ID:</span>
                  <span className="ml-2 text-sm text-gray-900">{selectedRowData.id}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">다운로드 링크:</span>
                  <div className="mt-1 p-2 bg-white border rounded text-sm break-all">
                    {downloadLinks[selectedRowData.id] || ''}
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                위 링크가 사용자에게 이메일로 전송됩니다.
              </p>
            </div>

            <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
              <Button 
                className="h-9" 
                text="취소" 
                onClick={() => setIsConfirmModalOpened(false)} 
                type="secondary" 
                size="large" 
                disabled={isLoading}
              />
              <Button 
                className="h-9" 
                text={isLoading ? "전송 중..." : "전송"} 
                onClick={onClickSend} 
                type="blue" 
                size="large" 
                disabled={isLoading}
              />
            </div>
          </ModalInner>
        </Modal>
      )}
    </div>
  );
};
export default PaidSchedule;
